import React, { CSSProperties, ReactNode, useMemo, useRef, useState } from 'react';
import DataTableGovernance from '../../../components/main/DataTableGovernance/DataTableGovernance';
import GovernanceFilterBar from '../../../components/main/GovernanceFilterBar/GovernanceFilterBar';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import RequestLibraryCards from '../../../components/main/RequestLibraryCards/RequestLibraryCards';
import {
    RequestStateEnum,
    DisciplineEnum,
    useGetAssignedRequestsLibrarySummaryQuery,
    useGetAssignedRequestsQuery,
    useGetMyRequestsLibrarySummaryQuery,
    useGetMyRequestsQuery,
    ContentVersionSortInput,
} from '../../../generated/graphql';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

type MyContentProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

const tabs = ['Assigned to Me', 'Submitted by Me'] as const;

function filterRequests(data: any[] | undefined | null, search: string) {
    if (!data || !search) return data;
    const lower = search.toLowerCase();
    return data.filter((r: any) =>
        r.name?.toLowerCase().includes(lower) ||
        r.client?.name?.toLowerCase().includes(lower) ||
        r.discipline?.toLowerCase().includes(lower) ||
        r.status?.toLowerCase().includes(lower) ||
        r.createdBy?.name?.toLowerCase().includes(lower)
    );
}

const MyContent = (props: MyContentProps) => {
    const { style } = props;
    const [selected, setSelected] = useState<number>(0);
    const [assignedSort, setAssignedSort] = useState<TableSort | null>({ field: 'updatedTime', direction: 'desc' });
    const [disciplineNeedAction, setDisciplineNeedAction] = useState<DisciplineEnum | null>(null);
    const [needActionSearch, setNeedActionSearch] = useState('');
    const [assignedClientIds, setAssignedClientIds] = useState<string[]>([]);
    const [assignedLibraryIds, setAssignedLibraryIds] = useState<string[]>([]);

    const [submittedSort, setSubmittedSort] = useState<TableSort | null>({ field: 'updatedTime', direction: 'desc' });
    const [disciplineMyRequests, setDisciplineMyRequests] = useState<DisciplineEnum | null>(null);
    const [myRequestsSearch, setMyRequestsSearch] = useState('');
    const [submittedClientIds, setSubmittedClientIds] = useState<string[]>([]);
    const [submittedLibraryIds, setSubmittedLibraryIds] = useState<string[]>([]);
    const loadingMoreAssigned = useRef(false);
    const loadingMoreSubmitted = useRef(false);

    const needActionRequests = useGetAssignedRequestsQuery({
        variables: {
            state: RequestStateEnum.Open,
            discipline: disciplineNeedAction,
            order: createGraphQLOrder<ContentVersionSortInput>(assignedSort, { field: 'updatedTime', direction: 'desc' }),
            where: assignedClientIds.length || assignedLibraryIds.length ? {
                clientId: assignedClientIds.length ? { in: assignedClientIds } : undefined,
                libraryId: assignedLibraryIds.length ? { in: assignedLibraryIds } : undefined,
            } : undefined,
        }
    });
    const assignedSummary = useGetAssignedRequestsLibrarySummaryQuery({
        variables: {
            state: RequestStateEnum.Open,
            discipline: disciplineNeedAction,
        },
    });

    const myRequests = useGetMyRequestsQuery({
        variables: {
            state: null,
            discipline: disciplineMyRequests,
            order: createGraphQLOrder<ContentVersionSortInput>(submittedSort, { field: 'updatedTime', direction: 'desc' }),
            where: submittedClientIds.length || submittedLibraryIds.length ? {
                clientId: submittedClientIds.length ? { in: submittedClientIds } : undefined,
                libraryId: submittedLibraryIds.length ? { in: submittedLibraryIds } : undefined,
            } : undefined,
        },
    });
    const submittedSummary = useGetMyRequestsLibrarySummaryQuery({
        variables: {
            state: null,
            discipline: disciplineMyRequests,
        },
    });

    const filteredNeedAction = useMemo(() => filterRequests(needActionRequests.data?.AssignedRequests?.nodes, needActionSearch), [needActionRequests.data, needActionSearch]);
    const filteredMyRequests = useMemo(() => filterRequests(myRequests.data?.MyRequests?.nodes, myRequestsSearch), [myRequests.data, myRequestsSearch]);

    const fetchMoreAssigned = async () => {
        const pageInfo = needActionRequests.data?.AssignedRequests?.pageInfo;
        if (!pageInfo?.hasNextPage || !pageInfo.endCursor || loadingMoreAssigned.current) return;
        loadingMoreAssigned.current = true;
        try {
            await needActionRequests.fetchMore({
                variables: {
                    after: pageInfo.endCursor,
                    order: createGraphQLOrder<ContentVersionSortInput>(assignedSort, { field: 'updatedTime', direction: 'desc' }),
                    where: assignedClientIds.length || assignedLibraryIds.length ? {
                        clientId: assignedClientIds.length ? { in: assignedClientIds } : undefined,
                        libraryId: assignedLibraryIds.length ? { in: assignedLibraryIds } : undefined,
                    } : undefined,
                },
                updateQuery: (previous, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.AssignedRequests) return previous;
                    return {
                        ...previous,
                        AssignedRequests: {
                            ...fetchMoreResult.AssignedRequests,
                            nodes: [
                                ...(previous.AssignedRequests?.nodes ?? []),
                                ...(fetchMoreResult.AssignedRequests.nodes ?? []),
                            ],
                        },
                    };
                },
            });
        } finally {
            loadingMoreAssigned.current = false;
        }
    };

    const fetchMoreSubmitted = async () => {
        const pageInfo = myRequests.data?.MyRequests?.pageInfo;
        if (!pageInfo?.hasNextPage || !pageInfo.endCursor || loadingMoreSubmitted.current) return;
        loadingMoreSubmitted.current = true;
        try {
            await myRequests.fetchMore({
                variables: {
                    after: pageInfo.endCursor,
                    order: createGraphQLOrder<ContentVersionSortInput>(submittedSort, { field: 'updatedTime', direction: 'desc' }),
                    where: submittedClientIds.length || submittedLibraryIds.length ? {
                        clientId: submittedClientIds.length ? { in: submittedClientIds } : undefined,
                        libraryId: submittedLibraryIds.length ? { in: submittedLibraryIds } : undefined,
                    } : undefined,
                },
                updateQuery: (previous, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.MyRequests) return previous;
                    return {
                        ...previous,
                        MyRequests: {
                            ...fetchMoreResult.MyRequests,
                            nodes: [
                                ...(previous.MyRequests?.nodes ?? []),
                                ...(fetchMoreResult.MyRequests.nodes ?? []),
                            ],
                        },
                    };
                },
            });
        } finally {
            loadingMoreSubmitted.current = false;
        }
    };

    return (
        <div className="page-container governance-list-page" style={style} data-testid="GovernanceMyContent">
            {/* Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">My Requests</h1>
                    <p className="page-subtitle">Requests assigned to you or submitted by you</p>
                </div>
                <RefreshButton
                    onRefresh={() => {
                        needActionRequests.refetch();
                        assignedSummary.refetch();
                        myRequests.refetch();
                        submittedSummary.refetch();
                    }}
                    className="btn-secondary"
                    iconSize={16}
                />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border-default)' }}>
                {tabs.map((tab, i) => (
                    <button
                        key={tab}
                        onClick={() => setSelected(i)}
                        className={selected === i ? 'tab-item-active' : 'tab-item'}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 min-h-0 relative">
                {selected === 0 && (
                    <div className="governance-content-stack">
                        <RequestLibraryCards
                            requests={assignedSummary.data?.AssignedRequests?.nodes}
                            selectedClientIds={assignedClientIds}
                            selectedLibraryIds={assignedLibraryIds}
                            onClientsSelect={setAssignedClientIds}
                            onLibrariesSelect={setAssignedLibraryIds}
                        />
                        <GovernanceFilterBar
                            discipline={disciplineNeedAction}
                            onDisciplineChange={setDisciplineNeedAction}
                            isAscending={assignedSort?.field === 'updatedTime' && assignedSort.direction === 'asc'}
                            onSortChange={(isAscending) => setAssignedSort({ field: 'updatedTime', direction: isAscending ? 'asc' : 'desc' })}
                        />
                        <div className="flex-1 min-h-0">
                            <DataTableGovernance
                                onSearchBoxChanged={setNeedActionSearch}
                                onRefetch={() => needActionRequests.refetch()}
                                fetchMore={fetchMoreAssigned}
                                hasMore={needActionRequests.data?.AssignedRequests?.pageInfo.hasNextPage}
                                isLoading={needActionRequests.loading}
                                data={filteredNeedAction}
                                sort={assignedSort}
                                onSortChange={setAssignedSort}
                            />
                        </div>
                    </div>
                )}
                {selected === 1 && (
                    <div className="governance-content-stack">
                        <RequestLibraryCards
                            requests={submittedSummary.data?.MyRequests?.nodes}
                            selectedClientIds={submittedClientIds}
                            selectedLibraryIds={submittedLibraryIds}
                            onClientsSelect={setSubmittedClientIds}
                            onLibrariesSelect={setSubmittedLibraryIds}
                        />
                        <GovernanceFilterBar
                            discipline={disciplineMyRequests}
                            onDisciplineChange={setDisciplineMyRequests}
                            isAscending={submittedSort?.field === 'updatedTime' && submittedSort.direction === 'asc'}
                            onSortChange={(isAscending) => setSubmittedSort({ field: 'updatedTime', direction: isAscending ? 'asc' : 'desc' })}
                        />
                        <div className="flex-1 min-h-0">
                            <DataTableGovernance
                                onSearchBoxChanged={setMyRequestsSearch}
                                onRefetch={() => myRequests.refetch()}
                                fetchMore={fetchMoreSubmitted}
                                hasMore={myRequests.data?.MyRequests?.pageInfo.hasNextPage}
                                isLoading={myRequests.loading}
                                data={filteredMyRequests}
                                sort={submittedSort}
                                onSortChange={setSubmittedSort}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyContent;
